import React, { useState, useEffect } from "react";
import theme from "../../../assets/theme";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import useLoader from "../../../hooks/useLoader";
import { caseVisibilityType } from "../../../helpers/constants";
import styled, { keyframes } from "styled-components";
import { Modal } from "@material-ui/core";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import BundleService from "../../../services/BundleService";

const HearingBundleShared = ({
  id,
  modal,
  setModal,
  caseId,
  visibilityType,
  setRefreshDetails,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [visibility, setVisibility] = useState();

  useEffect(() => {
    if (modal) {
      setVisibility(visibilityType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  const changeVisibilityType = async (value) => {
    try {
      setLoader({ state: true, message: "Updating visibility..." });
      const payload = { visibilityType: value };
      const res = await BundleService.hearingVisibilityType(
        payload,
        caseId,
        id,
      );
      if (res) {
        enqueueSnackbar("Visibility updated successfully", {
          variant: "success",
        });
      }
      setVisibility(value);
      setRefreshDetails(true);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setModal(false);
    }
  };

  const getVisibilityInfo = (type) => {
    switch (type) {
      case "all":
        return {
          label: "Public",
          color: "#16a34a",
          bg: "#dcfce7",
          border: "#bbf7d0",
          icon: "🌐",
        };
      case "others":
        return {
          label: "Shared",
          color: "#d97706",
          bg: "#fef3c7",
          border: "#fde68a",
          icon: "🔓",
        };
      default:
        return {
          label: "Private",
          color: "#dc2626",
          bg: "#fee2e2",
          border: "#fecaca",
          icon: "🔒",
        };
    }
  };

  const info = getVisibilityInfo(visibility);

  return (
    <Modal open={modal} onClose={() => setModal(false)}>
      <Overlay>
        <ModalCard role="presentation">
          <TopAccent />

          <ModalHeader>
            <IconCircle>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </IconCircle>
            <HeaderText>
              <ModalTitle>Share Settings</ModalTitle>
              <ModalSubtitle>Control who can view this bundle</ModalSubtitle>
            </HeaderText>
            <CloseBtn onClick={() => setModal(false)} aria-label="Close">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <ModalDivider />

          <ModalBody>
            {/* Current status indicator */}
            {visibility && (
              <StatusCard color={info.color} bg={info.bg} border={info.border}>
                <StatusIcon>{info.icon}</StatusIcon>
                <StatusInfo>
                  <StatusTag>Current visibility</StatusTag>
                  <StatusLabel color={info.color}>{info.label}</StatusLabel>
                </StatusInfo>
              </StatusCard>
            )}

            <FieldGroup>
              <FieldLabel>Select visibility level</FieldLabel>
              <SelectWrapper>
                <CustomSelect
                  menuItemValues={caseVisibilityType}
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value)}
                />
              </SelectWrapper>
            </FieldGroup>

            <VisibilityGuide>
              <GuideTitle>Visibility Options</GuideTitle>
              <GuideRow>
                <GuideBadge color="#16a34a" bg="#dcfce7">
                  {"🌐"} Public
                </GuideBadge>
                <GuideDesc>Visible to all participants</GuideDesc>
              </GuideRow>
              <GuideRow>
                <GuideBadge color="#d97706" bg="#fef3c7">
                  {"🔓"} Shared
                </GuideBadge>
                <GuideDesc>Visible to CM and Neutral</GuideDesc>
              </GuideRow>
              <GuideRow>
                <GuideBadge color="#dc2626" bg="#fee2e2">
                  {"🔒"} Private
                </GuideBadge>
                <GuideDesc>Only visible to you</GuideDesc>
              </GuideRow>
            </VisibilityGuide>
          </ModalBody>

          <ModalFooter>
            <PrimaryOutlinedCloseCTAButton
              style={{ flex: 1 }}
              onClick={(e) => {
                e.preventDefault();
                setModal(false);
              }}
            >
              Cancel
            </PrimaryOutlinedCloseCTAButton>
            <PrimaryCTAButton
              style={{ flex: 1 }}
              onClick={(e) => {
                e.preventDefault();
                changeVisibilityType(visibility);
              }}
            >
              Update
            </PrimaryCTAButton>
          </ModalFooter>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default HearingBundleShared;

/* ── Animations ── */
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

/* ── Styles ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 450px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.2),
    0 8px 24px rgba(0, 131, 140, 0.12);
  animation: ${fadeSlideIn} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 20px 20px 0 0;
    top: auto;
    bottom: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
  }
`;

const TopAccent = styled.div`
  height: 4px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 22px 18px;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e6f7f8 0%, #b2e8eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #00838c;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  color: #293461;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    #e2e8f0 30%,
    #e2e8f0 70%,
    transparent
  );
  margin: 0 22px;
`;

const ModalBody = styled.div`
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ bg }) => bg};
  border: 1.5px solid ${({ border }) => border};
  border-radius: 14px;
`;

const StatusIcon = styled.span`
  font-size: 22px;
  flex-shrink: 0;
`;

const StatusInfo = styled.div``;

const StatusTag = styled.p`
  margin: 0 0 2px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const StatusLabel = styled.p`
  margin: 0;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${({ color }) => color};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #334155;
`;

const SelectWrapper = styled.div`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
  }
`;

const VisibilityGuide = styled.div`
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GuideTitle = styled.p`
  margin: 0 0 6px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const GuideRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GuideBadge = styled.span`
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${({ color }) => color};
  background: ${({ bg }) => bg};
  padding: 3px 10px;
  border-radius: 100px;
  white-space: nowrap;
`;

const GuideDesc = styled.span`
  font-size: 12px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 22px 22px;

  @media (max-width: 380px) {
    flex-direction: column-reverse;
  }
`;
